import { useEffect, useState } from "react";
import { Col, Divider, Flex, Progress, Row, Select, Spin, Statistic, Tabs, Typography } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined, TagOutlined } from "@ant-design/icons";
import { callGetLottosByStatusNoPageable } from "../../../services/lottoService";
import { callGetStatisticsByLottoId } from "../../../services/statisticsService";
import { formatCurrency, showNotificationTopEnd } from "../../../util/utils";
import StatisticsHours from "./StatisticsHours/StatisticsHours";
import StatisticsStates from "./StatisticsStates/StatisticsStates";

const { Title } = Typography;


export default function Statistics() {
    const [lottoList, setLottoList] = useState([]);
    const [lotto, setLotto] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(false);


    useEffect(() => {
        getLottoList();
    }, []);


    useEffect(() => {
        if (lotto) {
            getStatistics();
        }
        // eslint-disable-next-line
    }, [lotto]);


    const getStatistics = async () => {
        try {
            setLoading(true);
            const response = await callGetStatisticsByLottoId(lotto?.lottoId);
            if (response?.status === 200) {
                setStatistics(response.data);
            }
        } catch (err) {
            showNotificationTopEnd("error", "Error cargando estadísticas");
        } finally {
            setLoading(false);
        }
    };


    const getLottoList = async () => {
        try {
            const response = await callGetLottosByStatusNoPageable({ status: ["ACTIVE", "CLOSED", "COMPLETED"] });
            if (response?.status === 200) {
                setLottoList(response.data);
            }
        } catch (err) {
            showNotificationTopEnd("error", "Error cargando sorteos");
        }
    };


    const items = [
        {
            key: '1',
            label: 'Vendidos/Hora',
            children: <StatisticsHours data={statistics?.ticketsSoldHours} lotto={lotto} amount={statistics?.ticketsSoldAmount}
                type="Tickets Vendidos" color="#52c41aff" colorText='verde' icon={<CheckCircleOutlined />} />,
        },
        {
            key: '2',
            label: 'Reservados/Hora',
            children: <StatisticsHours data={statistics?.ticketsReservedHours} lotto={lotto} amount={statistics?.ticketsReservedAmount}
                type="Tickets Reservados" color="#d4af37" colorText='amarillo' icon={<ClockCircleOutlined />} />,
        },
        {
            key: '3',
            label: 'Vendidos/Estado',
            children: <StatisticsStates data={statistics?.ticketsSoldState} lotto={lotto} amount={statistics?.ticketsSoldAmount}
                type="Tickets Vendidos" color="#52c41aff" colorText='verde' icon={<CheckCircleOutlined />} />,
        },
        {
            key: '4',
            label: 'Reservados/Estado',
            children: <StatisticsStates data={statistics?.ticketsReservedState} lotto={lotto} amount={statistics?.ticketsReservedAmount}
                type="Tickets Reservados" color="#d4af37" colorText='amarillo' icon={<ClockCircleOutlined />} />,
        },
    ];


    return (
        <Flex vertical>
            <Row gutter={[16, 16]} justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                <Col span="auto">
                    <Title level={3}>Estadísticas de Sorteos</Title>
                </Col>
            </Row>

            <Flex align="center">
                <Title style={{ marginTop: 10, minWidth: 100, fontWeight: "normal" }} level={5}>Selecciona el Sorteo</Title>
                <Select
                    showSearch
                    placeholder="Selecciona el sorteo"
                    filterOption={false}
                    style={{ width: "100%" }}
                    size="large"
                    listHeight={300}
                    options={lottoList?.map(lotto => ({
                        value: lotto.lottoId,
                        label: `ID: ${lotto.lottoId} - #${lotto.lottoNumber} ${lotto.title} - ${lotto.status}`,
                    }))}
                    onChange={(value) => {
                        const selected = lottoList.find(l => l.lottoId === value);
                        setLotto(selected);
                    }}
                />
            </Flex>

            <Spin spinning={loading}>
                {statistics && (
                    <>
                        <br /><br /><br />
                        <Row gutter={[16, 24]} justify="space-around">
                            <Col xs={12} sm={6}>
                                <Statistic
                                    title="Total Tickets"
                                    value={lotto?.ticketAmount / lotto?.opportunities}
                                    prefix={<TagOutlined />}
                                    valueStyle={{ color: '#000' }}
                                />
                            </Col>

                            <Col xs={12} sm={6}>
                                <Statistic
                                    title="Vendidos"
                                    value={statistics?.ticketsSoldAmount}
                                    prefix={<CheckCircleOutlined />}
                                    valueStyle={{ color: '#52c41a' }}
                                    suffix={<small style={{ fontSize: '12px', color: '#8c8c8c' }}>({((statistics?.ticketsSoldAmount / (lotto?.ticketAmount / lotto?.opportunities)) * 100).toFixed(2)}%)</small>}
                                />
                            </Col>

                            <Col xs={12} sm={6}>
                                <Statistic
                                    title="Reservados"
                                    value={statistics?.ticketsReservedAmount}
                                    prefix={<ClockCircleOutlined />}
                                    valueStyle={{ color: '#d4af37' }}
                                    suffix={<small style={{ fontSize: '12px', color: '#8c8c8c' }}>({((statistics?.ticketsReservedAmount / (lotto?.ticketAmount / lotto?.opportunities)) * 100).toFixed(2)}%)</small>}
                                />
                            </Col>

                            <Col xs={12} sm={6}>
                                <Statistic
                                    title="Disponibles"
                                    value={(lotto?.ticketAmount / lotto?.opportunities) - (statistics?.ticketsSoldAmount + statistics?.ticketsReservedAmount)}
                                    valueStyle={{ color: '#a63827' }}
                                />
                            </Col>

                            <Col xs={12} sm={6}>
                                <Statistic
                                    title="Precio por boleto"
                                    value={lotto?.ticketPrice}
                                    valueStyle={{ color: '#000' }}
                                    formatter={formatCurrency}
                                />
                            </Col>

                            <Col xs={12} sm={6}>
                                <Statistic
                                    title="Ganancia Total Obtenida"
                                    value={statistics?.ticketsSoldAmount * lotto?.ticketPrice}
                                    valueStyle={{ color: '#298801ff' }}
                                    formatter={formatCurrency}
                                />
                            </Col>

                            <Col xs={12} sm={6}>
                                <Statistic
                                    title="Ganancia Ideal"
                                    value={statistics?.ticketsReservedAmount * lotto?.ticketPrice}
                                    valueStyle={{ color: '#1890ff' }}
                                    formatter={formatCurrency}

                                />
                            </Col>

                            <Col xs={12} sm={6}>
                                <Statistic
                                    title="Ganancia Máxima"
                                    value={(lotto?.ticketAmount / lotto?.opportunities) * lotto?.ticketPrice}
                                    valueStyle={{ color: '#cf1322' }}
                                    formatter={formatCurrency}
                                />
                            </Col>
                        </Row>

                        <Divider style={{ margin: '24px 0 12px 0' }} />

                        <div style={{ padding: '0 10px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ fontWeight: '600', color: '#595959' }}>Progreso de Ventas</span>
                                <span style={{ fontWeight: 'bold', color: '#a63827' }}>{(((statistics?.ticketsSoldAmount + statistics?.ticketsReservedAmount) / (lotto?.ticketAmount / lotto?.opportunities)) * 100).toFixed(2)}% Completado</span>
                            </div>

                            <Progress
                                percent={((statistics?.ticketsSoldAmount + statistics?.ticketsReservedAmount) / (lotto?.ticketAmount / lotto?.opportunities)) * 100}
                                success={{ percent: ((statistics?.ticketsSoldAmount / (lotto?.ticketAmount / lotto?.opportunities)) * 100), strokeColor: '#52c41a' }}
                                strokeColor="#d4af37"
                                showInfo={false}
                                size={["default", 12]}
                                style={{ marginBottom: '10px' }}
                            />

                            <div style={{ display: 'flex', gap: '15px', fontSize: '12px' }}>
                                <span><span style={{ color: '#52c41a' }}>●</span> Vendidos</span>
                                <span><span style={{ color: '#d4af37' }}>●</span> Reservados</span>
                                <span><span style={{ color: '#f0f0f0' }}>●</span> Libres</span>
                            </div>
                        </div>

                        <br /><br /><br />
                        <Tabs size="middle" centered defaultActiveKey="1" items={items} />
                    </>
                )}
            </Spin>
        </Flex>
    );
}